import {
  actionColumn,
  AiSlimBanner,
  BoundSelectField,
  Button,
  ButtonMenu,
  column,
  Css,
  emptyCell,
  fieldCell,
  getTableStyles,
  GridColumn,
  GridDataRow,
  GridTable,
  MenuItem,
  ModalBody,
  ModalFooter,
  ModalHeader,
  simpleHeader,
  SimpleHeaderAndData,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { BoundAiNumberField, BoundAiTextField } from "src/components/formFields/ai";
import { pendingSources } from "src/components/formFields/ai/AiProposalValue";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  aiProposalsConfig,
  AiProposalsFormValue,
  entityIsRemoved,
  hasPendingProposals,
  toAiFieldProposalFormValue,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import {
  findAiFieldProposal,
  isAiProposedDeletion,
  parseAiNumber,
} from "src/components/formFields/ai/utils/bindAiProposal";
import {
  aiFieldBinding,
  reconcilePendingAiProposals,
  resolvePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { UploaderProvider } from "src/contexts/UploaderContext";
import {
  AiEntityProposalType,
  AiFieldProposalStatus,
  AssetInput,
  IncrementalCollectionOp,
  Maybe,
  ReadyPlanAssetType,
  SaveProgramDataInput,
} from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  PlanPackageElevationsV2_ReadyPlanOptionFragment,
  PlanPackageElevationsV2Query,
  useElevationCatalogV2Query,
  useSavePlanPackageElevationMutation,
} from "src/routes/libraries/plan-package-v2/components/ElevationsTab.generated";
import { PlanPackageAssetUploader } from "src/routes/libraries/plan-package-v2/components/PlanPackageAssetUploader";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { planPackageAiSourcesRoute } from "src/routing/domains/librariesPaths";
import { openNewTab } from "src/utils/window";

type AddEditElevationModalProps = {
  planPackage: PlanPackageElevationsV2Query["planPackage"];
  elevation?: PlanPackageElevationsV2_ReadyPlanOptionFragment;
  proposal?: AiFormAepFragment;
};

export function AddEditElevationModal(props: AddEditElevationModalProps) {
  const { planPackage, elevation, proposal } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageElevationMutation();
  // The elevation catalog to pick from; also loaded when editing so the (disabled) field can show its current value
  const catalogQuery = useElevationCatalogV2Query();

  const formState = useFormState({
    config: elevationFormConfig,
    init: {
      input: [elevation, proposal] as const,
      map: ([elevation, proposal]) => ({
        globalOptionId: elevation?.globalOption.id,
        name: elevation?.name,
        code: elevation?.code,
        coverPhoto: elevation && maybeCoverPhoto(elevation),
        programData: elevation?.programData ?? {},
        fieldProposals: (proposal?.fieldProposals ?? []).map((fp) => toAiFieldProposalFormValue(fp)),
      }),
    },
  });

  const hasProposals = useComputed(() => hasPendingProposals(formState.fieldProposals.rows), [formState]);
  /** The proposal for one field, or the entity removal, which applies to every field. */
  function proposalFor(fieldName: string) {
    return findAiFieldProposal(formState.fieldProposals.rows, fieldName);
  }
  const rows = useComputed(() => createRows(formState.programData, proposalFor), [formState]);

  async function onSave() {
    // Save commits what the AI fields are showing, so anything left pending is treated as accepted since
    // user has seen it.
    reconcilePendingAiProposals(
      rows.flatMap((row) =>
        row.kind === "data" && row.data.field
          ? [aiFieldBinding(row.data.field, parseAiNumber, row.data.aiProposal)]
          : [],
      ),
      AiFieldProposalStatus.Accepted,
    );
    // That leaves the code and name, which have no field to apply; `updateElevation` writes an accepted rename
    resolvePendingAiProposals(formState.fieldProposals.rows, AiFieldProposalStatus.Accepted);
    const saved = elevation ? await updateElevation(elevation) : await createElevation();
    triggerNotice({ message: `Elevation ${elevation ? "updated" : "added"}!` });
    closeModal();
    if (saved && saved.version.id !== planPackage.version.id) navigateToRpav(saved.version.id);
  }

  async function updateElevation(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
    const { coverPhoto } = formState.value;
    const pdChanges = formState.programData.changedValue;
    const record = ownProgramData(elevation);
    const proposals = toResolveInputs(formState.fieldProposals.changedValue);
    // Ignoring the import's proposed elevation drops the row it created for review.
    const removed = !!proposal && entityIsRemoved(proposal.type.code, formState.fieldProposals.rows);
    // A created elevation was built with its proposed name, so only accepting an existing one's rename writes it
    const rename = proposal?.type.code === AiEntityProposalType.Update ? proposalFor("customName") : undefined;
    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          options: [
            {
              id: elevation.id,
              ...(removed ? { active: false } : {}),
              // A null fieldValue clears the custom name, not an empty string
              ...(rename?.status.value === AiFieldProposalStatus.Accepted
                ? { name: rename.fieldValue.value ?? null }
                : {}),
              ...(formState.coverPhoto.dirty ? { assets: buildAssetsInput(elevation, coverPhoto) } : {}),
            },
          ],
          ...(proposals.nonEmpty ? { proposals } : {}),
          ...(Object.keys(pdChanges).nonEmpty
            ? {
                programDatas: [
                  {
                    op: IncrementalCollectionOp.Include,
                    // Sending both id and readyPlanOptionIds reads as a new, unversioned record
                    ...(record ? { id: record.id } : { readyPlanOptionIds: [elevation.id] }),
                    programData: pdChanges,
                  },
                ],
              }
            : {}),
        },
      },
    });
    return data?.savePlanPackage.planPackage;
  }

  async function createElevation() {
    const { globalOptionId, coverPhoto } = formState.value;
    const pdChanges = formState.programData.changedValue;
    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          // Elevations come from the global option catalog, so the code/name follow the `globalOptionId`
          options: [
            {
              active: true,
              globalOptionId,
              ...(coverPhoto ? { assets: buildAssetsInput(undefined, coverPhoto) } : {}),
            },
          ],
        },
      },
    });
    const saved = data?.savePlanPackage.planPackage;
    // Program data needs the RPO to exist first, so save it against the id we just got back
    const created = saved?.options.find((o) => o.globalOption.id === globalOptionId && o.active);
    if (created && Object.keys(pdChanges).nonEmpty) {
      // The new option already has a program data record; a second one is rejected as a duplicate
      const record = ownProgramData(created);
      await save({
        variables: {
          input: {
            id: planPackage.id,
            programDatas: [
              {
                op: IncrementalCollectionOp.Include,
                ...(record ? { id: record.id } : { readyPlanOptionIds: [created.id] }),
                programData: pdChanges,
              },
            ],
          },
        },
      });
    }
    return saved;
  }

  // Catalog elevations already on this plan package can't be added twice
  const usedGlobalOptionIds = new Set(
    planPackage.options
      .filter((o) => o.active && o.type.isElevation && o.id !== elevation?.id)
      .map((o) => o.globalOption.id),
  );

  // Keyed off presence rather than status, so the fields don't swap back to the select as they're resolved
  const hasIdentityProposals = useComputed(
    () => formState.fieldProposals.rows.some((p) => identityFieldNames.includes(p.fieldName.value)),
    [formState],
  );
  // A cleared `customName` proposal means "use the catalog's name"
  const catalogName = catalogQuery.data?.globalOptions.find((go) => go.id === elevation?.globalOption.id)?.name;

  return (
    <>
      <ModalHeader>{elevation ? "Edit Elevation" : "Add Elevation"}</ModalHeader>
      {hasProposals && (
        <AiSlimBanner
          title={`Review ${formState.fieldProposals.rows.length} Suggested Changes`}
          action={{
            label: "Ignore All",
            onClick: () => resolvePendingAiProposals(formState.fieldProposals.rows, AiFieldProposalStatus.Rejected),
          }}
        />
      )}
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div css={Css.gray900.mt3.$}>
            Enter the values for this elevation (not deltas). Options will modify these values.
          </div>
          {hasIdentityProposals ? (
            // Split the catalog pick so a proposed code and name each read as AI suggestions
            <>
              <BoundAiTextField
                label="Name"
                field={formState.name}
                aiProposal={proposalFor("customName")}
                parser={(raw) => raw ?? catalogName}
                disabled={lockedReason}
                visuallyDisabled={false}
              />
              <BoundAiTextField
                label="Option Code"
                field={formState.code}
                aiProposal={proposalFor("globalOption")}
                disabled={lockedReason}
                visuallyDisabled={false}
              />
            </>
          ) : (
            <BoundSelectField
              label="Elevation"
              field={formState.globalOptionId}
              options={catalogQuery.data?.globalOptions ?? []}
              getOptionLabel={(go) => `${go.code} - ${go.name}`}
              disabledOptions={[...usedGlobalOptionIds].map((id) => ({ value: id, reason: "Already in use" }))}
              disabled={!!elevation && lockedReason}
            />
          )}
          <GridTable id="elevationProgramData" columns={columns} rows={rows} style={tableStyle} />
          <div>
            <div css={Css.sm.gray700.mb1.$}>Cover Photo</div>
            <UploaderProvider>
              <Observer>
                {() => (
                  <PlanPackageAssetUploader
                    assetUrl={formState.coverPhoto.value?.previewUrl}
                    assetType={ReadyPlanAssetType.CoverPhoto}
                    saveAsset={(file) => formState.coverPhoto.set(toCoverPhoto(file))}
                  />
                )}
              </Observer>
            </UploaderProvider>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Observer>
          {() => (
            <Button
              label={hasProposals ? "Save AI Changes" : "Save"}
              variant={hasProposals ? "ai" : "primary"}
              disabled={!formState.valid && formState.errors.join(", ")}
              onClick={onSave}
            />
          )}
        </Observer>
      </ModalFooter>
    </>
  );
}

type ProgramDataRow = {
  label: string;
  tooltip?: string;
  field?: FieldState<Maybe<number>>;
  /** Set when the server derives the value, so the row displays it instead of taking input. */
  readOnlyValue?: number;
  numFractionDigits?: number;
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
};

type Row = SimpleHeaderAndData<ProgramDataRow>;

const tableStyle = {
  // `flexible` rather than `fixed` so long labels can wrap instead of forcing a horizontal scroll.
  // Top-aligned so a proposal's struck-through original grows the row downward instead of pulling its label off the field.
  ...getTableStyles({ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "flexible", vAlign: "top" }),
  // Keep the table within the modal instead of letting the label column push it into a horizontal scroll
  rootCss: Css.mw0.w100.$,
};

type CoverPhotoValue = Pick<AssetInput, "contentType" | "fileName" | "s3Key" | "fileSizeInBytes"> & {
  previewUrl?: Maybe<string>;
};

type ElevationProgramData = Pick<
  SaveProgramDataInput,
  | "netBuildableSqft"
  | "grossBuildableSqft"
  | "grossBelowGroundSqft"
  | "sellableAboveGroundSqft"
  | "sellableBelowGroundSqft"
  | "permittableSqft"
  | "imperviousSqft"
  | "buildingHeightInFeet"
  | "widthInFeet"
  | "depthInFeet"
>;

type ElevationFormValue = AiProposalsFormValue & {
  globalOptionId: Maybe<string>;
  /** Display-only, so a proposed catalog code and name can each show as AI suggestions; never saved. */
  name: Maybe<string>;
  code: Maybe<string>;
  coverPhoto: Maybe<CoverPhotoValue>;
  programData: ElevationProgramData;
};

const elevationFormConfig: ObjectConfig<ElevationFormValue> = {
  ...aiProposalsConfig,
  globalOptionId: { type: "value", rules: [required] },
  name: { type: "value" },
  code: { type: "value" },
  coverPhoto: { type: "value" },
  programData: {
    type: "object",
    config: {
      netBuildableSqft: { type: "value" },
      grossBuildableSqft: { type: "value" },
      grossBelowGroundSqft: { type: "value" },
      sellableAboveGroundSqft: { type: "value" },
      sellableBelowGroundSqft: { type: "value" },
      permittableSqft: { type: "value" },
      imperviousSqft: { type: "value" },
      buildingHeightInFeet: { type: "value" },
      widthInFeet: { type: "value" },
      depthInFeet: { type: "value" },
    },
  },
};

/** The proposal field names for an elevation's catalog code and name. */
const identityFieldNames = ["globalOption", "customName"];

const lockedReason = "Elevations follow their catalog option, which can't change once added.";

/** The record holding just this elevation's program data, as opposed to one shared with an option. */
function ownProgramData(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
  return elevation.programDatas.find((pd) => pd.readyPlanOptions.length === 1);
}

function maybeCoverPhoto(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment): Maybe<CoverPhotoValue> {
  const cover = elevation.assets.find((a) => a.active && a.isCoverPhoto);
  return cover && { previewUrl: cover.asset.previewUrl };
}

function toCoverPhoto(file: AssetUppyFile | undefined): Maybe<CoverPhotoValue> {
  return file
    ? {
        contentType: file.type,
        fileName: file.name,
        s3Key: file.meta.s3Key,
        fileSizeInBytes: file.data?.size ?? file.size ?? 0,
        previewUrl: file.meta.previewUrl,
      }
    : null;
}

function buildAssetsInput(
  elevation: Maybe<PlanPackageElevationsV2_ReadyPlanOptionFragment>,
  coverPhoto: Maybe<CoverPhotoValue>,
) {
  const kept = (elevation?.assets ?? []).filter((a) => !(a.active && a.isCoverPhoto)).map((a) => ({ id: a.id }));
  if (!coverPhoto) return kept;
  const { previewUrl, ...asset } = coverPhoto;
  return [...kept, { type: ReadyPlanAssetType.CoverPhoto, asset }];
}

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Elevation Details", w: 1, data: ({ label, tooltip }) => ({ content: label, tooltip }) }),
  column<Row>({
    header: "Value",
    data: ({ label, field, readOnlyValue, numFractionDigits, aiProposal }) =>
      field ? (
        fieldCell(
          <BoundAiNumberField
            label={label}
            field={field}
            numFractionDigits={numFractionDigits}
            aiProposal={aiProposal}
            hideAiSources
          />,
        )
      ) : (
        <span css={Css.gray700.$}>{readOnlyValue ?? "—"}</span>
      ),
    align: "right",
    w: "120px",
  }),
  column<Row>({ header: "UoM", data: (row) => unitFor(row), w: "60px" }),
  actionColumn<Row>({
    header: emptyCell,
    // Boxed to the compact field's height so the trigger centers on the field, not on the original hanging below it
    // TODO: Followup DS-411 remove the box once Beam can align content beside a field to its input
    data: (row) =>
      hasFieldReview(row)
        ? fieldCell(
            <div css={Css.df.aic.hPx(32).$}>
              <ProgramDataRowMenu row={row} />
            </div>,
          )
        : emptyCell,
    w: "40px",
  }),
];

/** A pending edit to this row's own field; an entity removal is reviewed from the banner instead. */
function hasFieldReview(row: ProgramDataRow): boolean {
  return (
    !!row.field &&
    row.aiProposal?.status.value === AiFieldProposalStatus.Pending &&
    !isAiProposedDeletion(row.aiProposal)
  );
}

type ProgramDataRowMenuProps = { row: ProgramDataRow };

/** Review actions for one proposed field, matching the Program Data tab's row menus. */
function ProgramDataRowMenu(props: ProgramDataRowMenuProps) {
  const { row } = props;
  const params = useRouteParams<PlanPackageDetailsParams>();
  const resolve = (decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) => () =>
    row.field && reconcilePendingAiProposals([aiFieldBinding(row.field, parseAiNumber, row.aiProposal)], decision);
  const sources = pendingSources(row.aiProposal);
  const items: MenuItem[] = [
    { label: "Accept Changes", ai: true, onClick: resolve(AiFieldProposalStatus.Accepted) },
    { label: "Ignore Change", ai: true, onClick: resolve(AiFieldProposalStatus.Rejected) },
    ...(sources
      ? [
          {
            label: "View Source",
            ai: true,
            onClick: () => openNewTab(planPackageAiSourcesRoute.url(params, { afpId: sources.afpId })),
          },
        ]
      : []),
  ];
  return <ButtonMenu trigger={{ icon: "verticalDots" }} placement="right" items={items} />;
}

/** Program data has no unit in the schema, so each field's unit comes from its name. */
function unitFor(row: ProgramDataRow) {
  return row.field?.key.endsWith("InFeet") ? "ft" : "sf";
}

function createRows(
  pd: ObjectState<ElevationFormValue>["programData"],
  proposalFor: (fieldName: string) => ObjectState<AiFieldProposalFormValue> | undefined,
): GridDataRow<Row>[] {
  const programDataDetails: ProgramDataRow[] = [
    {
      label: "Net Buildable Square Footage",
      field: pd.netBuildableSqft,
      tooltip: "Total square footage that can be built within the allowable building area.",
    },
    {
      label: "Gross Square Footage",
      field: pd.grossBuildableSqft,
      tooltip: "Total square footage of the building, including all above- and below-ground areas.",
    },
    { label: "Gross Below Ground Square Footage", field: pd.grossBelowGroundSqft },
    {
      label: "Sellable Square Footage",
      readOnlyValue: (pd.sellableAboveGroundSqft.value ?? 0) + (pd.sellableBelowGroundSqft.value ?? 0),
      tooltip:
        "Total square footage counted toward the home\u2019s sellable area. Calculated from Sellable Above Ground and Sellable Below Ground square footage.",
    },
    { label: "Sellable Above Ground Square Footage", field: pd.sellableAboveGroundSqft },
    { label: "Sellable Below Ground Square Footage", field: pd.sellableBelowGroundSqft },
    {
      label: "Unfinished Below Ground Square Footage",
      readOnlyValue: (pd.grossBelowGroundSqft.value ?? 0) - (pd.sellableBelowGroundSqft.value ?? 0),
      tooltip: "Below-ground square footage that is unfinished and not included in finished sellable area.",
    },
    { label: "Permittable Square Footage", field: pd.permittableSqft },
    {
      label: "Impervious SF",
      field: pd.imperviousSqft,
      tooltip:
        "Total area of impervious surfaces associated with the home, such as the building footprint and other non-permeable surfaces.",
    },
    { label: "Height", field: pd.buildingHeightInFeet, numFractionDigits: 2 },
    { label: "Width", field: pd.widthInFeet, numFractionDigits: 2 },
    { label: "Depth", field: pd.depthInFeet, numFractionDigits: 2 },
  ];
  return [
    simpleHeader,
    ...programDataDetails.map((d) => ({
      id: d.label,
      kind: "data" as const,
      data: { ...d, aiProposal: d.field && proposalFor(d.field.key) },
    })),
  ];
}
